// Pure logic of the new potato spuit program. No React, no HTTP.
//
// A program is a list of spray rows. Each row = Category, Target and Product (as in the customer's prototype), a pack, a dose per spray
// and the weeks that are ticked. The picked Category + Target + Product is one CHOICE (table choices); the plan product is saved with
// exactly that choice, and the product's other recommended doses are shown as a hint. The program turns into whole boxes per pack
// for the whole farm, priced with the same functions the Hybrid screen uses (calcVolumes + bundleCheck).

const num = (v) => (v === null || v === undefined || v === "" || Number.isNaN(Number(v)) ? 0 : Number(v));
const ceilBoxes = (x) => Math.max(0, Math.ceil(x - 1e-9)); // 5.0000000001 must not become 6; never -0
const norm = (s) => String(s || "").toLowerCase().split(/\s+/).filter(Boolean).join(" ");

export const OTHER = "Ander";
/** Screen-only target "Ander" (no database row): picking it turns the Product cell into a text box. */
export const ANDER_TARGET = -1;
const isOtherChoice = (c) => !!c.is_parent || norm(c.product) === "ander";

/** Every choice of the crop: all its products are offered (the old "Chris's list only" filter is gone). */
export const offeredChoices = (all) => all || [];

/**
 * One entry per product: its first choice (what the plan product is saved with), its recommended doses, its resistance group.
 * Ander (the "other product" option) comes last. @param all  /api/spray_program/choices/{crop}
 */
export function offeredProducts(all) {
  const byId = new Map();
  offeredChoices(all).forEach((c) => {
    const p = byId.get(c.product_id) || { productId: c.product_id, name: c.product, other: !!c.is_parent || norm(c.product) === "ander", group: c.resistance_group || null, choices: [] };
    p.choices.push(c);
    byId.set(c.product_id, p);
  });
  const list = [...byId.values()].map((p) => {
    p.choices.sort((a, b) => a.choice_id - b.choice_id);
    return { ...p, choice: p.choices[0], doses: [...new Set(p.choices.map((c) => c.dosage).filter((d) => d > 0))].sort((a, b) => a - b) };
  });
  return list.sort((a, b) => (a.other === b.other ? a.name.localeCompare(b.name) : a.other ? 1 : -1));
}

export const blankRow = (periodCount = 0) => ({
  key: typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : String(Math.random()),
  categoryId: null,
  targetId: null,
  choiceId: null,
  productId: null,
  packRowId: null,
  dose: "",
  otherName: "",
  group: "",
  weeks: Array.from({ length: periodCount }, () => false),
});

/** pricing2 rows of a product, biggest pack first. */
export const packOptions = (pricing, productId) =>
  (pricing || []).filter((p) => p.product_id === productId).sort((a, b) => num(b.full_pack_volume) - num(a.full_pack_volume));

/** Row after picking a product: the first choice's dose and the biggest pack. Weeks stay. */
export function pickProduct(products, pricing, row, productId) {
  const p = (products || []).find((x) => x.productId === productId);
  if (!p) return { ...row, categoryId: null, targetId: null, choiceId: null, productId: null, packRowId: null, dose: "", group: "", otherName: "" };
  const packs = p.other ? [] : packOptions(pricing, p.productId);
  return {
    ...row,
    categoryId: p.choice.category_id,
    targetId: p.choice.target_id,
    choiceId: p.choice.choice_id,
    productId: p.productId,
    packRowId: packs.length ? packs[0].id : null,
    dose: p.choice.dosage > 0 ? p.choice.dosage : "",
    group: "",
    otherName: "",
  };
}

// ---------- Category -> Target -> Product (the prototype's pick-lists) ----------
const byName = (a, b) => String(a.name).localeCompare(String(b.name));

/** [{id, name}] the categories of the crop. */
export function categoriesOf(all) {
  const m = new Map();
  (all || []).forEach((c) => m.set(c.category_id, { id: c.category_id, name: c.category }));
  return [...m.values()].sort(byName);
}

/** [{id, name}] the targets of one category. */
export function targetsOf(all, categoryId) {
  const m = new Map();
  (all || []).filter((c) => c.category_id === categoryId).forEach((c) => m.set(c.target_id, { id: c.target_id, name: c.target }));
  const list = [...m.values()].sort(byName);
  if (list.length && (all || []).some(isOtherChoice)) list.push({ id: ANDER_TARGET, name: OTHER }); // always last
  return list;
}

/** One entry per product that has a choice for this category + target: { choiceId, productId, name, dose, other }. Ander last. */
export function productsOf(all, categoryId, targetId) {
  const m = new Map();
  (all || [])
    .filter((c) => c.category_id === categoryId && c.target_id === targetId)
    .forEach((c) => {
      const cur = m.get(c.product_id);
      if (!cur || c.choice_id < cur.choiceId) m.set(c.product_id, { choiceId: c.choice_id, productId: c.product_id, name: c.product, dose: c.dosage, other: !!c.is_parent || norm(c.product) === "ander" });
    });
  return [...m.values()].sort((a, b) => (a.other === b.other ? byName(a, b) : a.other ? 1 : -1));
}

/** Row after picking a category: target and product are cleared (the weeks stay). */
export const pickCategory = (row, categoryId) => ({ ...row, categoryId: categoryId || null, targetId: null, choiceId: null, productId: null, packRowId: null, dose: "", group: "", otherName: "" });

/**
 * Row after picking a target: the product is cleared. The screen-only target "Ander" sets the Ander product at once
 * (a choice of this category if there is one, else the lowest Ander choice); the user then types the product name.
 */
export const pickTarget = (row, targetId, all) => {
  const cleared = { ...row, targetId: targetId || null, choiceId: null, productId: null, packRowId: null, dose: "", group: "", otherName: "" };
  if (targetId !== ANDER_TARGET) return cleared;
  const others = (all || []).filter(isOtherChoice).sort((a, b) => a.choice_id - b.choice_id);
  const c = others.find((x) => x.category_id === row.categoryId) || others[0];
  return c ? { ...cleared, choiceId: c.choice_id, productId: c.product_id } : cleared;
};

/** Row after picking a product of the category + target: this exact choice, its dose and the biggest pack. */
export function pickChoice(all, pricing, row, choiceId) {
  const c = (all || []).find((x) => x.choice_id === choiceId);
  if (!c) return { ...row, choiceId: null, productId: null, packRowId: null, dose: "", group: "", otherName: "" };
  const other = !!c.is_parent || norm(c.product) === "ander";
  const packs = other ? [] : packOptions(pricing, c.product_id);
  return { ...row, categoryId: c.category_id, targetId: c.target_id, choiceId: c.choice_id, productId: c.product_id, packRowId: packs.length ? packs[0].id : null, dose: c.dosage > 0 ? c.dosage : "", group: "", otherName: "" };
}

// pricing2 uses 999 for "no maximum dosage"
const NO_LIMIT = 999;
/** "Reg" / "Bo maks" / "Onder min" / "Geen limiete", on the product's total dose per ha over all its rows. */
export function doseCheck(volHa, min, max) {
  const lo = num(min) > 0 ? num(min) : null;
  const hi = num(max) > 0 && num(max) < NO_LIMIT ? num(max) : null;
  if (!(volHa > 0)) return "";
  if (hi !== null && volHa > hi + 1e-9) return "Bo maks";
  if (lo !== null && volHa < lo - 1e-9) return "Onder min";
  return lo !== null || hi !== null ? "Reg" : "Geen limiete";
}

// ---------- the program in numbers ----------
export function computeProgram({ rows, products, pricing, hectares }) {
  const ha = num(hectares);
  const byPack = new Map((pricing || []).map((p) => [p.id, p]));
  const byProduct = new Map((products || []).map((p) => [p.productId, p]));
  const out = (rows || []).map((row) => {
    const product = byProduct.get(row.productId) || null;
    const pack = byPack.get(row.packRowId) || null;
    const sprays = (row.weeks || []).filter(Boolean).length;
    const dose = num(row.dose);
    const volHa = sprays * dose;
    const other = !!product && product.other;
    const priced = !!pack && !other;
    const group = product ? product.group || row.group || "" : "";
    const choice = product ? product.choices.find((c) => c.choice_id === row.choiceId) || product.choice : null;
    return {
      row, product, choice, pack, sprays, dose, volHa, litres: volHa * ha, other, priced,
      complete: !!product, group, needsGroup: !!product && volHa > 0 && !group,
    };
  });

  // Dose limits are checked on the product's total over all its rows and packs
  const productVolHa = new Map();
  out.forEach((r) => {
    if (r.product && r.priced) productVolHa.set(r.product.productId, (productVolHa.get(r.product.productId) || 0) + r.volHa);
  });
  out.forEach((r) => {
    r.productVolHa = r.product ? productVolHa.get(r.product.productId) || 0 : 0;
    r.check = r.priced ? doseCheck(r.productVolHa, r.pack.tier2_min_dosage, r.pack.max_dosage) : "";
  });

  // Whole boxes per pack for the whole farm
  const litresByPack = new Map();
  out.forEach((r) => {
    if (r.priced && r.litres > 0) litresByPack.set(r.pack.id, (litresByPack.get(r.pack.id) || 0) + r.litres);
  });
  const lines = [...litresByPack].map(([rowId, litres]) => ({ key: "pack-" + rowId, rowId, boxes: ceilBoxes(litres / num(byPack.get(rowId).full_pack_volume)), litres }));

  // Products that cannot be priced (Ander, or not in the price list): listed with their volume only
  const unpriced = new Map();
  out.forEach((r) => {
    if (r.priced || !r.complete || r.litres <= 0) return;
    const name = r.other ? (r.row.otherName || "").trim() || OTHER : r.product.name;
    unpriced.set(name, (unpriced.get(name) || 0) + r.litres);
  });

  // Resistance groups: how often, and in which weeks, each mode of action is sprayed
  const groups = new Map();
  out.filter((r) => r.complete && r.sprays > 0).forEach((r) => {
    const k = r.group || "Unknown";
    const g = groups.get(k) || { group: k, products: new Set(), sprays: 0, weeks: new Set() };
    g.products.add(r.other ? (r.row.otherName || "").trim() || OTHER : r.product.name);
    g.sprays += r.sprays;
    r.row.weeks.forEach((on, i) => on && g.weeks.add(i));
    groups.set(k, g);
  });
  const groupsSummary = [...groups.values()]
    .map((g) => ({ group: g.group, products: [...g.products], sprays: g.sprays, weeks: [...g.weeks].sort((a, b) => a - b) }))
    .sort((a, b) => a.group.localeCompare(b.group));

  return { rows: out, lines, unpriced: [...unpriced].map(([name, litres]) => ({ name, litres })), groupsSummary, needsGroup: out.some((r) => r.needsGroup) };
}

// ---------- Silvalis dependants: add the partner products the bundle needs ----------
/**
 * Rows for the partners that are NOT in the program yet. Each partner row copies the weeks of the bundle's main product and takes the bundle dose.
 * @param check  one entry of bundleCheck() ({mainProductId, partners:[{productId, name, dose, sprays, ok}]})
 */
export function dependantRows({ check, products, pricing, rows, periodCount }) {
  const mainRow = (rows || []).find((r) => r.productId === check.mainProductId && (r.weeks || []).some(Boolean));
  const present = new Set((rows || []).map((r) => r.productId).filter(Boolean));
  const added = [];
  const skipped = [];
  (check.partners || []).forEach((p) => {
    if (present.has(p.productId)) {
      if (!p.ok) skipped.push(p.name);
      return;
    }
    const product = (products || []).find((x) => x.productId === p.productId && !x.other);
    if (!product) {
      skipped.push(p.name);
      return;
    }
    const weeks = mainRow ? [...mainRow.weeks] : Array.from({ length: periodCount }, (_, i) => i < Math.min(p.sprays || 1, periodCount));
    added.push({ ...pickProduct(products, pricing, blankRow(periodCount), product.productId), dose: p.dose, weeks });
  });
  return { added, skipped };
}
